import React from 'react';

import { Box, Typography } from '@mui/material';

import { tokens } from '../../theme-next';
import { dedupeDocLinksByType, docLinkGroupOf, HORS_FORMULAIRE_COLOR_KEYS, sortDocLinks } from './docLinksGrouping';
import type { ViewerFile } from './InlineFileViewer';

interface DocLinksPanelProps {
  files: ViewerFile[];
  currentIndex: number;
  onChange: (index: number) => void;
}

/**
 * Liste verticale des liens de pièces, EMBARQUÉE en bas du bloc « Document x/y »
 * du viewer (collée, hauteur plafonnée + scroll interne : sur 13", une liste
 * détachée suivait le scroll de la colonne de droite et devenait illisible —
 * retour Luc 2026-07-30). Contenu (retours François 2026-07-29) :
 *  - pièces contrôlées via le formulaire d'abord, ordre logique du dossier ;
 *  - pièces contrôlées autrement (cession, mandat, assurance, CT) isolées en
 *    fin, chacune avec SA couleur ;
 *  - pièces non reconnues en dernier, libellées par nom de fichier.
 * Le tri est purement visuel : chaque lien garde son index d'origine, la
 * navigation du viewer n'est pas affectée.
 */
export const DocLinksPanel = ({ files, currentIndex, onChange }: DocLinksPanelProps) => {
  if (files.length <= 1) return null;

  // Un seul lien par type (les scans multi-pièces déposés en plusieurs
  // exemplaires produisaient autant de lignes que de fichiers), puis tri par
  // groupe. Les deux passes sont pures et testées séparément.
  const sorted = sortDocLinks(
    dedupeDocLinksByType(
      files.map((f, idx) => ({ f, idx, docType: f.docType, confidence: f.confidence })),
      currentIndex,
    ),
  );
  const firstHorsFormulaireIdx = sorted.findIndex(({ docType }) => docLinkGroupOf(docType) === 'hors_formulaire');
  const firstNonReconnueIdx = sorted.findIndex(({ docType }) => docLinkGroupOf(docType) === 'non_reconnue');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '8px 16px 12px',
        borderTop: `1px solid ${tokens.color.border}`,
        flexShrink: 0,
        backgroundColor: tokens.color.white,
        // Plafond en vh : la liste rétrécit sur petit écran (13") pour rendre
        // sa hauteur à l'aperçu, et reste confortable sur grand écran. Le
        // scroll interne prend le relais quand il y a beaucoup de pièces.
        maxHeight: 'clamp(88px, 18vh, 180px)',
        overflowY: 'auto',
      }}
    >
      {sorted.map(({ f, idx, docType }, position) => {
        const isActive = idx === currentIndex;
        const group = docLinkGroupOf(docType);
        const colorKey = docType ? HORS_FORMULAIRE_COLOR_KEYS[docType] : undefined;
        const groupColors = colorKey
          ? {
              backgroundColor: tokens.color[`${colorKey}Light`],
              borderColor: tokens.color[`${colorKey}Border`],
              color: tokens.color[`${colorKey}Dark`],
            }
          : group === 'non_reconnue'
            ? {
                backgroundColor: tokens.color.surface,
                borderColor: tokens.color.borderDark,
                borderStyle: 'dashed',
                color: tokens.color.textSecondary,
              }
            : {
                backgroundColor: tokens.color.bgMid,
                borderColor: tokens.color.border,
                color: tokens.color.textSecondary,
              };

        const separator =
          position === firstHorsFormulaireIdx
            ? 'Contrôlées hors formulaire'
            : position === firstNonReconnueIdx
              ? 'Pièces non reconnues'
              : null;

        return (
          <React.Fragment key={`link-${f.url}-${idx}`}>
            {separator && (
              <Typography
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: tokens.color.textMuted,
                  marginTop: position === 0 ? 0 : '8px',
                }}
              >
                {separator}
              </Typography>
            )}
            <Box
              onClick={() => onChange(idx)}
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: `${tokens.radius.sm}px`,
                borderWidth: '0.5px',
                borderStyle: 'solid',
                cursor: 'pointer',
                ...groupColors,
                // L'état ACTIF (jaune) prime toujours sur la couleur de groupe.
                ...(isActive && {
                  backgroundColor: tokens.color.yellowLight,
                  borderColor: tokens.color.yellow,
                  color: tokens.color.dark,
                }),
                '&:hover': {
                  backgroundColor: tokens.color.yellowLight,
                  borderColor: tokens.color.yellow,
                  color: tokens.color.dark,
                },
              }}
            >
              {f.label}
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};
